import { Injectable } from '@angular/core';
import { ChipItem } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { SelectedLocation } from '@next/core-lib/user';

import { FeedAdviceSettingsComponent } from '../components/feed-advice-settings/feed-advice-settings.component';
import { FeedAdviceTableComponent } from '../components/feed-advice-table/feed-advice-table.component';
import {
  Consumption,
  Phase,
  SchemeTableRow,
  TableScheme,
  FeedAdviceLine,
  FeedAdviceType,
  GenderTypes,
  PhaseTransitionTypes,
  InputSystemType,
  FeedAdvice,
  FeedingType,
} from '../models/feed-advice.model';

@Injectable({
  providedIn: 'root',
})
export class SchemeSupportService {
  constructor(
    private readonly locale: LocaleService,
    private readonly dialogService: DialogService,
  ) {}

  private static calculateFeedPerDayByPhase(
    line: FeedAdviceLine | SchemeTableRow,
    phase: Phase,
  ): number {
    if (!phase.product) {
      return 0;
    }
    const percentage =
      (line.consumptions.find((c) => c.phaseNumber === phase.phaseNumber)
        ?.productIntakeInPercentage ?? 0) / 100;
    return percentage ? percentage * (line.energyValuePerDay / phase.product.ewKg) : 0;
  }

  calculateFeedPerDay(
    line: FeedAdviceLine | SchemeTableRow,
    phases: Phase[],
    inputSystem: InputSystemType,
    isPigletsFeedadvice: boolean,
  ): number {
    let feedperday = 0;
    phases.forEach((phase) => {
      if (!phase.product) {
        return 0;
      }
      let calculatedEwKg = 0;
      if (inputSystem === InputSystemType.ENERGYDAY) {
        calculatedEwKg =
          (line.energyValuePerDay / phase.product.ewKg) *
          ((line.consumptions.find((c) => c.phaseNumber === phase.phaseNumber)
            ?.productIntakeInPercentage ?? 0) /
            100);
      } else {
        calculatedEwKg =
          (this.calculateEnergyValue(line, phases, isPigletsFeedadvice) / phase.product.ewKg) *
          ((line.consumptions.find((c) => c.phaseNumber === phase.phaseNumber)
            ?.productIntakeInPercentage ?? 0) /
            100);
      }

      feedperday += Math.max(calculatedEwKg, 0);
    });
    if (isPigletsFeedadvice) {
      feedperday *= 1000;
    }
    return feedperday;
  }

  calculateEnergyValue(
    line: FeedAdviceLine | SchemeTableRow,
    phases: Phase[],
    isPigletsFeedadvice: boolean,
  ): number {
    let energyValue = 0;
    const foodPerDay = isPigletsFeedadvice ? line.foodPerDay / 1000 : line.foodPerDay;
    phases.forEach((phase) => {
      if (!phase.product) {
        return 0;
      }
      const calculatedEwKg =
        foodPerDay *
        phase.product.ewKg *
        ((line.consumptions.find((c) => c.phaseNumber === phase.phaseNumber)
          ?.productIntakeInPercentage ?? 0) /
          100);
      energyValue += Math.max(calculatedEwKg, 0);
    });
    return energyValue;
  }

  calculateLysinePerDay(
    line: FeedAdviceLine | SchemeTableRow,
    phases: Phase[],
    inputSystem: InputSystemType,
    isPigletsFeedadvice: boolean,
  ): number {
    let calculatedLysKg = 0;
    for (let i = 0; i < phases.length; i++) {
      if (!phases[i].product) {
        return 0;
      }
      const percentage =
        line.consumptions.find((c) => c.phaseNumber === i + 1)?.productIntakeInPercentage ?? 0;
      calculatedLysKg += phases[i].product.lysKg * (percentage / 100);
    }
    let feedPerDay = this.calculateFeedPerDay(line, phases, inputSystem, isPigletsFeedadvice);
    if (isPigletsFeedadvice) {
      feedPerDay /= 1000;
    }

    const lysKg = feedPerDay * calculatedLysKg;
    return Math.round((lysKg + Number.EPSILON) * 100) / 100;
  }

  calculateCumulativeForAbruptTransition(
    currentLineNr: number,
    currentLineFeedPerDay: number,
    currentLineCorrectionFactor: number,
    previousLineCumulative: number,
    nextLineFeedPerDay: number,
  ): number {
    const totalWholeLine =
      nextLineFeedPerDay == null
        ? 7 * currentLineFeedPerDay
        : 7 * currentLineFeedPerDay + 3 * (nextLineFeedPerDay - currentLineFeedPerDay);

    const totalWholeLineWithFactor = totalWholeLine * currentLineCorrectionFactor;

    return currentLineNr === 1 || previousLineCumulative === null
      ? totalWholeLineWithFactor
      : previousLineCumulative + totalWholeLineWithFactor;
  }

  calculateCumulativeForGradualTransitionOrWeanedPiglets(
    phases: Phase[],
    currentLine: FeedAdviceLine | SchemeTableRow,
    nextLine: FeedAdviceLine | SchemeTableRow,
    previousLineCumulative: number,
    isPigletsFeedadvice: boolean,
  ): number {
    let calculatedValue = 0;
    let totalWholeLineWithFactor = 0;
    phases.forEach((phase) => {
      const currentLineFeedPerDayPerPhase = SchemeSupportService.calculateFeedPerDayByPhase(
        currentLine,
        phase,
      );
      if (isPigletsFeedadvice) {
        totalWholeLineWithFactor += currentLineFeedPerDayPerPhase;
      } else {
        const nextLineFeedPerDayPerPhase = nextLine
          ? SchemeSupportService.calculateFeedPerDayByPhase(nextLine, phase)
          : null;
        const totalWholeLine =
          nextLineFeedPerDayPerPhase == null
            ? 7 * currentLineFeedPerDayPerPhase
            : 7 * currentLineFeedPerDayPerPhase +
              3 * (nextLineFeedPerDayPerPhase - currentLineFeedPerDayPerPhase);
        totalWholeLineWithFactor += totalWholeLine * currentLine.correctionFactor;
      }
    });
    calculatedValue =
      currentLine.week === 1 || previousLineCumulative === null
        ? totalWholeLineWithFactor
        : previousLineCumulative + totalWholeLineWithFactor;
    return calculatedValue;
  }

  getGendersArray(isBarrow: boolean, isBoar: boolean, isGilt: boolean): string[] {
    const returnValue: string[] = [];
    if (isBarrow) {
      returnValue.push(this.locale.translate(`gender.${GenderTypes.BARROW}`).toLowerCase());
    }
    if (isBoar) {
      returnValue.push(this.locale.translate(`gender.${GenderTypes.BOAR}`).toLowerCase());
    }
    if (isGilt) {
      returnValue.push(this.locale.translate(`gender.${GenderTypes.GILT}`).toLowerCase());
    }
    return returnValue;
  }

  stringifyGenders(isBarrow: boolean, isBoar: boolean, isGilt: boolean): string {
    return (
      (isBarrow ? this.locale.translate(`gender.${GenderTypes.BARROW}`) : '') +
      (isGilt ? (isBarrow ? ', ' : '') + this.locale.translate(`gender.${GenderTypes.GILT}`) : '') +
      (isBoar
        ? (isBarrow || isGilt ? ', ' : '') + this.locale.translate(`gender.${GenderTypes.BOAR}`)
        : '')
    );
  }

  stringifyLocations(userLocations: SelectedLocation[], locationIds?: number[]): string {
    const selectedLocationIds = locationIds ?? [];
    return userLocations
      .filter((userlocation: SelectedLocation) => selectedLocationIds.includes(userlocation.id))
      .map((userlocation: SelectedLocation) => userlocation.name)
      .join(',');
  }

  getSelectedGenders(genders?: string[]): ChipItem<number>[] {
    return [
      {
        value: 1,
        name: this.locale.translate(`gender.${GenderTypes.BARROW}`),
        selected: genders
          ? genders.includes(this.locale.translate(`gender.${GenderTypes.BARROW}`).toLowerCase())
          : false,
      },
      {
        value: 2,
        name: this.locale.translate(`gender.${GenderTypes.BOAR}`),
        selected: genders
          ? genders.includes(this.locale.translate(`gender.${GenderTypes.BOAR}`).toLowerCase())
          : false,
      },
      {
        value: 3,
        name: this.locale.translate(`gender.${GenderTypes.GILT}`),
        selected: genders
          ? genders.includes(this.locale.translate(`gender.${GenderTypes.GILT}`).toLowerCase())
          : false,
      },
    ];
  }

  updateLineConsumptionsForPhaseTransition(
    line: FeedAdviceLine,
    phaseTransition: PhaseTransitionTypes,
  ): FeedAdviceLine {
    return {
      ...line,
      consumptions: [
        ...line.consumptions.map(
          (consumption) =>
            <Consumption>{
              phaseNumber: consumption.phaseNumber,
              productIntakeInPercentage:
                phaseTransition === PhaseTransitionTypes.ABRUPT &&
                consumption.productIntakeInPercentage !== 0 &&
                consumption.productIntakeInPercentage !== 100
                  ? null
                  : consumption.productIntakeInPercentage,
              dosatorPosition: consumption.dosatorPosition,
            },
        ),
      ].sort((a, b) => (a.phaseNumber > b.phaseNumber ? 1 : -1)),
    };
  }

  calculateTableRows(
    tableScheme: TableScheme,
    inputSystem: InputSystemType,
    feedingType?: FeedingType,
  ): SchemeTableRow[] {
    const rows = new Array<SchemeTableRow>();
    let previousCumulative: number = null;
    if (tableScheme) {
      for (let i = 0; i < tableScheme.feedAdviceLines.length; i++) {
        const nextLine =
          i < tableScheme.feedAdviceLines.length - 1 ? tableScheme.feedAdviceLines[i + 1] : null;
        const currentLine = tableScheme.feedAdviceLines[i];
        let cumulative: number;

        // Calculate Cumulative value for currentLine
        if (tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS) {
          cumulative = this.calculateCumulativeForGradualTransitionOrWeanedPiglets(
            tableScheme.phases,
            currentLine,
            nextLine,
            previousCumulative,
            tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS,
          );
        } else if (
          tableScheme.feedAdviceType === FeedAdviceType.REARING &&
          feedingType === FeedingType.MANUAL
        ) {
          cumulative = previousCumulative + 7 * currentLine.foodPerDay;
        } else {
          switch (tableScheme.phaseTransition) {
            case PhaseTransitionTypes.ABRUPT:
              cumulative = this.calculateCumulativeForAbruptTransition(
                tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS
                  ? currentLine.day
                  : currentLine.week,
                this.calculateFeedPerDay(
                  currentLine,
                  tableScheme.phases,
                  inputSystem,
                  tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS,
                ),
                tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS
                  ? 1
                  : currentLine.correctionFactor,
                previousCumulative,
                nextLine == null
                  ? null
                  : this.calculateFeedPerDay(
                      nextLine,
                      tableScheme.phases,
                      inputSystem,
                      tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS,
                    ),
              );
              break;

            case PhaseTransitionTypes.GRADUAL:
              cumulative = this.calculateCumulativeForGradualTransitionOrWeanedPiglets(
                tableScheme.phases,
                currentLine,
                nextLine,
                previousCumulative,
                tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS,
              );
              break;
          }
        }

        switch (tableScheme.feedAdviceType) {
          case FeedAdviceType.FATTENING:
            rows.push(<SchemeTableRow>{
              line: currentLine.week,
              week: currentLine.week,
              day: currentLine.week * 7 - 6,
              weight: currentLine.weight,
              correctionFactor:
                !Object.prototype.hasOwnProperty.call(currentLine, 'correctionFactor') ||
                !currentLine.correctionFactor
                  ? currentLine.week < 15
                    ? 1.0
                    : 0.9
                  : currentLine.correctionFactor,
              energyValuePerDay: currentLine.energyValuePerDay,
              intestinalDigestibleLysinePerDay: this.calculateLysinePerDay(
                currentLine,
                tableScheme.phases,
                inputSystem,
                false,
              ),
              consumptions: currentLine.consumptions,
              foodPerDay: this.calculateFeedPerDay(
                currentLine,
                tableScheme.phases,
                inputSystem,
                false,
              ),
              cumulative,
              referenceSchemeIntestinalDigestibleLysinePerDay:
                currentLine.referenceSchemeIntestinalDigestibleLysinePerDay,
            });
            break;
          case FeedAdviceType.REARING:
            rows.push(<SchemeTableRow>{
              line: currentLine.week,
              week: currentLine.week,
              weekAge:
                currentLine.weekAge ??
                Math.round((currentLine.week * 7 - 6 + tableScheme.rearingAge) / 7),
              age: currentLine.age ?? currentLine.week * 7 - 6 + tableScheme.rearingAge,
              weight: currentLine.weight,
              correctionFactor:
                !Object.prototype.hasOwnProperty.call(currentLine, 'correctionFactor') ||
                !currentLine.correctionFactor
                  ? currentLine.week < 15
                    ? 1.0
                    : 0.9
                  : currentLine.correctionFactor,
              energyValuePerDay: currentLine.energyValuePerDay,
              intestinalDigestibleLysinePerDay: this.calculateLysinePerDay(
                currentLine,
                tableScheme.phases,
                inputSystem,
                false,
              ),
              consumptions: currentLine.consumptions,
              foodPerDay: this.calculateFeedPerDay(
                currentLine,
                tableScheme.phases,
                inputSystem,
                false,
              ),
              cumulative,
              referenceSchemeIntestinalDigestibleLysinePerDay:
                currentLine.referenceSchemeIntestinalDigestibleLysinePerDay,
              feedingTimesPerDay: currentLine.feedingTimesPerDay,
            });
            break;
          case FeedAdviceType.WEANED_PIGLETS:
            rows.push(<SchemeTableRow>{
              line: currentLine.day,
              day: currentLine.day,
              age: tableScheme.pigletWeaningAge + currentLine.day,
              weight: currentLine.weight,
              energyValuePerDay: currentLine.energyValuePerDay,
              intestinalDigestibleLysinePerDay: this.calculateLysinePerDay(
                currentLine,
                tableScheme.phases,
                inputSystem,
                true,
              ),
              consumptions: currentLine.consumptions,
              foodPerDay: this.calculateFeedPerDay(
                currentLine,
                tableScheme.phases,
                inputSystem,
                tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS,
              ),
              cumulative,
              referenceSchemeIntestinalDigestibleLysinePerDay:
                currentLine.referenceSchemeIntestinalDigestibleLysinePerDay,
            });
            break;
        }
        previousCumulative = cumulative;
      }
    }
    return rows;
  }

  validateFeedAdvice(
    advice: FeedAdvice,
    isWeanedPigletFeedadvice: boolean,
    isRearingFeedadvice: boolean,
    feedAdviceTableComponent: FeedAdviceTableComponent,
    settingsComponent: FeedAdviceSettingsComponent,
  ): boolean {
    const genderValidationError = !advice.isGilt && !advice.isBoar && !advice.isBarrow;
    settingsComponent.propertiesComponent.setGenderValidationError(genderValidationError);
    const validationErrors = feedAdviceTableComponent.linesWithValidationErrors;
    const productValidationErrors = feedAdviceTableComponent.productsWithValidationErrors;
    const pigletWeaningWeightValidationErrors =
      isWeanedPigletFeedadvice &&
      settingsComponent.propertiesComponent.formGroup.controls.weaningWeight.errors !== null;
    const pigletWeaningAgeValidationErrors =
      isWeanedPigletFeedadvice &&
      settingsComponent.propertiesComponent.formGroup.controls.weaningAge.errors !== null;

    const rearingDayValidationErrors =
      isRearingFeedadvice &&
      settingsComponent.propertiesComponent.formGroupRearingAge.controls.rearingAge.errors !== null;

    if (
      advice.name.trim().length > 0 &&
      validationErrors.length === 0 &&
      productValidationErrors.length === 0 &&
      !genderValidationError &&
      !pigletWeaningWeightValidationErrors &&
      !pigletWeaningAgeValidationErrors &&
      !rearingDayValidationErrors
    ) {
      return true;
    }

    // one or more validation errors have occurred, so the dialog message is created.
    let description = '';
    if (advice.name.trim().length === 0) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.feedadvice-name',
      )}</li>`;
    }
    if (pigletWeaningWeightValidationErrors) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.weaning-weight',
      )}</li>`;
    }
    if (pigletWeaningAgeValidationErrors) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.weaning-age',
      )}</li>`;
    }
    if (rearingDayValidationErrors) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.rearing-age',
      )}</li>`;
    }
    if (productValidationErrors.length > 0) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.product',
      )}</li>`;
    }
    if (genderValidationError) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.gender',
      )}</li>`;
    }
    if (validationErrors.some((err) => err.type === 'percentage')) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.percentage',
      )}</li>`;
    }
    if (validationErrors.some((err) => err.type === 'energyValue')) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.energy-value',
      )}</li>`;
    }
    if (validationErrors.some((err) => err.type === 'foodPerDay')) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.food-per-day',
      )}</li>`;
    }
    if (validationErrors.some((err) => err.type === 'weight')) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.weight',
      )}</li>`;
    }
    if (validationErrors.some((err) => err.type === 'correctionFactor')) {
      description += `<li>${this.locale.translate(
        'feed-advice-detail.validation-error.description.correction-factor',
      )}</li>`;
    }

    this.dialogService.notify({
      title: this.locale.translate('feed-advice-detail.validation-error.title'),
      text: `<ul>${description}</ul>`,
    });

    return false;
  }
}
